import {cs, cst, cstAssign}      from "@Common/react/chain-services";
import {fragments}               from "@Common/react/fragments";
import {Invoke}                  from "@Common/react/invoke";
import {Load2}                   from "@Common/react/load2";
import {Static}                  from "@Common/react/static";
import {Types}                   from "@Common/types";
import {isDevXd}                 from "@Common/ui-components/debug/is-dev";
import {gnv, setNestedValue}     from "@Common/utils/arr-path";
import {arrEquals, replaceIndex} from "@Common/utils/collections";
import {chain}                   from "@Common/utils/fs";

import {getBindingResults} from "./cc-editor/get-binding-results";
import {formatText}        from "./tools/format-text";

export const TextFields = ({
/*  PROPS  ************************************************************************************************************/
    ccEditor,
    side,
    onChanged,
    ccConfig,
    userData,
    next
}:{
/*  PROP TYPES  *******************************************************************************************************/
    ccEditor    : Types.Common.Tools.CcIntegration.Models.CcEditor,
    side        : any,
    onChanged   : any,
    ccConfig    : any,
    userData    : any,
    next        : any,
}) => cst<{
/*  CS ASSIGNMENT NODE TYPES  *****************************************************************************************/
    textFields  : any,
    batchSave   : any,
}>(
    cstAssign("textFields", ({}, next) => Load2({
        _key: ccEditor.initialized,
        fetch: async () => {

            if (!ccEditor.initialized) {
                return;
            }

            const product = await ccEditor.getProduct();
            let productModel = await product.getProductModel();
            let textFields = await productModel.getAllItems().filter((item)=>{
                return (item.type === "BoundedTextItem" || item.type === "PathBoundedTextItem") ;
            });
            textFields = textFields.filter((obj, index) => {
                return index === textFields.findIndex(
                    o => obj._name === o._name && obj._parentContainer?.parentComponent?._name === o._parentContainer?.parentComponent?._name);
            });
            // textFields = trimTextFields(textFields);

            // remove any HTML markup provided on text fields for CC6 and above - markup already excluded with CC5
            if (ccEditor.ccVersionMajor >= 6) textFields.map(stripMarkupFromTextField);

            textFields = applyJsonToTextFields(textFields, ccConfig);

            return textFields;
        },
        next,
    })),
    cstAssign("batchSave", (_, next) => BatchSave({ccEditor, next})),
    ({textFields, batchSave}) => fragments(
        next(chain(
            textFields.value?.map(({id, text, label, name, type, side, inputMask, infoTip, rule, placeholders, index, textPermissions, originalText, locked, isRequired, isRichText}, i) => ({
                id, value: text, label, name, type, side, inputMask, infoTip, rule, placeholders, index, textPermissions, originalText, locked, isRequired, isRichText,
                onChange: (text) => {
                    let replace = replaceIndex(i, textFields.value, {
                        id, text, label, name, type, side, inputMask, infoTip, rule, placeholders, index, textPermissions, originalText, locked, isRequired, isRichText,
                    });
                    textFields.onChange(replace);

                    // add the update to the updates queue to be sent to CC
                    batchSave.saveBoundData([{side, name, text, placeholders, id, index, textPermissions, originalText, locked, isRichText, type}]);

                    let originalSide = side;
                    for(let x=0; x<=50; x++){
                        let currentValues = {};
                        replace.forEach((field) =>{
                            if (!currentValues[field['side']]) {
                                currentValues[field['side']] = {};
                            }
                            if (field['type'] !== 'officeSearch' && field['type'] !== 'userSearch' ) {
                                currentValues[field['side']][field['name']] = field['text'];
                            } else {
                                currentValues[field['side']][field['name']] = field['text'] ? JSON.parse(field['text']) : '';
                            }
                        });
                        const oldReplace = replace;
                        replace.map(({id, text, label, name, type, side, inputMask, infoTip, rule, placeholders, index, textPermissions, originalText, locked, isRequired, isRichText},i)=>{
                            if (
                                textPermissions?.['allowChangeText'] === false &&
                                side == originalSide &&
                                (type == 'hidden' || type == 'readOnly' || locked === true)
                            ) {
                                const bindingValue = ccConfig.templateBindingData[side]?.[name]?.value;
                                const textChange = formatText(
                                    inputMask,
                                    getBindingResults({...userData['sourceData'], ...currentValues[side]}, bindingValue)
                                );
                                if (text !== textChange) {
                                    replace[i] = {
                                        id, text: textChange, label, name, type, side, inputMask, infoTip, rule, placeholders, index, textPermissions, originalText, locked, isRequired, isRichText,
                                    };
                                    textFields.onChange(replace);

                                    // add the update to the updates queue to be sent to CC
                                    batchSave.saveBoundData([{side, name, text: textChange, placeholders, id, index, textPermissions, originalText, locked, isRichText, type}]);

                                }
                            }
                        })
                        if (arrEquals(oldReplace.map((r)=>r.text), replace.map((r)=>r.text))){
                            break;
                        }
                    }
                },
            })),
            (textFields) => textFields?.filter((tf) => tf.side === side).sort((a, b) => {
                if(isNaN(a.index)){
                    return -1
                }
                else if(isNaN(b.index)){
                    return 1
                }
                return (Number(a.index) > Number(b.index)) ? 1 : -1
            }),
        )),
        ccEditor.initialized && WatchChanges({
            ccEditor,
            batchSave,
            side,
            onDispatchedTextFieldChanged: ({id, text}) => {
                if (!textFields.value) return;
                onChanged?.();
            },
            onTextFieldChanged: ({id, text}) => {
                if (!textFields.value) {
                    return;
                }
                onChanged?.();
                const i = textFields.value.findIndex((r) => r.id === id);
                if( i < 0 ){
                    return;
                }

                const failSafeAttrs = (field, attrs) => {
                    for (const attr of attrs) {
                        if(!field[attr] && field["_" + attr]){
                            field[attr] = field["_" + attr];
                        }
                    }
                };

                const tf = {...textFields.value[i]};
                failSafeAttrs(tf, ["name", "textPermissions", "originalText", "locked", "placeholders", "text", "isRichText"]);
                if (tf.text !== text) {
                    let replaced = replaceIndex(i, textFields.value, {
                        ...tf,
                        text,
                        _text: text,
                    });
                    textFields.onChange(replaced);
                    let currentValues = {};
                    replaced.forEach((field) =>{
                        failSafeAttrs(tf, ["name", "type"]);

                        if (!currentValues[field['side']]) {
                            currentValues[field['side']] = {};
                        }

                        if (field['type'] !== 'officeSearch' && field['type'] !== 'userSearch' ) {
                            currentValues[field['side']][field['name']] = field['text'];
                        } else {
                            currentValues[field['side']][field['name']] = field['text'] ? JSON.parse(field['text']) : '';
                        }
                    });
                    for(let x=0; x<=50; x++){
                        let oldReplace = replaced;
                        replaced.forEach((field, index) =>{
                            failSafeAttrs(field,["name", "textPermissions", "originalText", "locked", "placeholders", "type", "text", "isRichText"] );

                            if (
                                field['textPermissions']['allowChangeText'] === false &&
                                field['side'] == tf['side'] &&
                                (field['type'] == 'hidden' || field['type'] == 'readOnly' || field['locked'] === true)
                            ) {
                                let bindingValue = (
                                    ccConfig.templateBindingData[field['side']]?.[field['name']]?.value
                                );
                                let mask = field.inputMask;
                                let text = formatText(mask, getBindingResults({...userData['sourceData'], ...currentValues[field['side']]}, bindingValue));
                                if(field['text'] != text){
                                    replaced[index] = {
                                        ...field,
                                        text,
                                        _text: text,
                                    };

                                    if (field['type'] !== 'officeSearch' && field['type'] !== 'userSearch' ) {
                                        currentValues[field['side']][field['name']] = field['text'];
                                    } else {
                                        currentValues[field['side']][field['name']] = field['text'] ? JSON.parse(field['text']) : '';
                                    }
                                }
                            }
                        });
                        if (arrEquals(oldReplace.map((r)=>r.text), replaced.map((r)=>r.text))){
                            break;
                        }
                    }
                    textFields.onChange(replaced);
                }
            },
            // onWarningsChanged: (value=true) => {
            //     changed.onChange(value ? Date.now() : null);
            // },
            onUnknownItemChanged: () => {
                // if (!changed.value && editing.value) {
                onChanged?.();
                // }
            },
        }),
    ),
);

const BatchSave = ({ccEditor, next}:{ccEditor:Types.Common.Tools.CcIntegration.Models.CcEditor, next:any}) => cs(
    ["_this", (_, next) => Static({
        getInitValue: () => ({
            isLoadingUserData: false,
            loadUserDataQueue: [],
            dispatchedChanges: [],
        }),
        next,
    })],
    ({_this}) => fragments(
        next({
            dispatchedChanges: _this.dispatchedChanges,
            changeDispatchedChanges: (fn) => _this.dispatchedChanges = fn(_this.dispatchedChanges),

            saveBoundData: (list) => {
                let ret = {};
                for (const {side, name, text, placeholders, isRichText, _isRichText, type} of list) {
                    ret = setNestedValue(
                        ret,
                        ["surfaces", side, name],
                        ((isRichText || _isRichText)
                            ? text.replace( /&(?!amp;|lt;)/gi, '&amp;').replace( /<(?![^<]*>)/gi, '&lt;')
                            : text
                        ).replace(/[\u0250-\u2012]|[\u2028-\u206F]|[\u2070-\ue007]/g, '')
                    );

                    // in CC6, replace all < and > characters with html entities to render the characters - these are treated as content markup if left intact
                    // we need the < > characters for custom variable span tags. Hidden type is for custom variable fields.
                    if ( ccEditor.ccVersionMajor >= 6 && type != 'hidden' ) {
                        ret = setNestedValue(
                            ret,
                            ["surfaces", side, name],
                            text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
                        );
                    }

                    if (placeholders?.["_collection"]?.["0"]){
                        ret = setNestedValue(ret, ["surfaces", side, placeholders["_collection"]["0"]["_name"] ], (isRichText || _isRichText ? text.replace( /&(?!amp;|lt;)/gi, '&amp;').replace( /<(?![^<]*>)/gi, '&lt;') : text).replace(/[\u0250-\u2012]|[\u2028-\u206F]|[\u2070-\ue007]/g, ''));
                    }

                    isDevXd() ? console.log("saveBoundData", ret): null;
                    _this.loadUserDataQueue.push({ret,list});
                }
            },
        }),
        Invoke({
            action: () => {

                // Starts the queuing system for sending updates to the CC editor. It ensures that only a single loadUserInfo
                // interaction can happen at a time. Each execution can only run if an update is not already in progress.

                setInterval(async () => {
                    if (_this.isLoadingUserData || !_this.loadUserDataQueue.length) return;

                    // mark the queue as running to prevent running while an update is already in progress
                    _this.isLoadingUserData = true;

                    // load the queue and clear it out immediately so that new updates will be added to the list for the next iteration
                    let userDataQueue = _this.loadUserDataQueue.splice(0, _this.loadUserDataQueue.length);

                    // merge all requests using only the last update to each field
                    let mergedUpdate = {surfaces: {}};
                    for (let i = userDataQueue.length - 1; i >= 0; i--) {

                        // loop through all surfaces and add to the merged update if not already set
                        Object.keys(userDataQueue[i].ret.surfaces).forEach(side => {
                            if (mergedUpdate.surfaces[side] === undefined) mergedUpdate.surfaces[side] = {};

                            // loop through all items on the surface and add to the merged update if not already set
                            Object.keys(userDataQueue[i].ret.surfaces[side]).forEach(field => {
                                if (mergedUpdate.surfaces[side][field] === undefined) {

                                    mergedUpdate.surfaces[side][field] = userDataQueue[i].ret.surfaces[side][field];

                                    // send the change list to dispatchedChanges so that the change event from CC will be ignored and not attempt to update the form field where the change originated from
                                    _this.dispatchedChanges.push(...userDataQueue[i].list);

                                }
                            });
                        });

                    }

                    // send the update to the editor
                    await ccEditor.callEditorMethod("loadUserInfo", mergedUpdate);

                    // mark the queue as not running
                    _this.isLoadingUserData = false;
                }, 500); // this has been tested as working down to 10ms, although not much value in going to such a short interval since the update time within the editor can never quite reach real-time
            },
        }),
    ),

);

const WatchChanges = ({ccEditor, batchSave, side, onDispatchedTextFieldChanged, onTextFieldChanged, onUnknownItemChanged}) => cs(
    () => Invoke({
        props: async (changedItem) => {
            if (isDevXd()) console.log("CcEditor itemChanged", changedItem);

            // await this.setWarnings();
            if ( (changedItem.type === "BoundedTextItem" || changedItem.type === "PathBoundedTextItem") && changedItem._locked === false ) {
                const dispatchedEventIndex = batchSave.dispatchedChanges.findIndex((r) =>
                    r.id === changedItem.id &&
                    r.text === changedItem.text
                );
                if (dispatchedEventIndex > -1) {
                    // This event came from this component, so we can discard it
                    batchSave.changeDispatchedChanges((_) => _.filter(x => x.id !== changedItem.id || (x.id === changedItem.id && x.text !== changedItem._text)));
                    isDevXd() ? console.log("Discarded event") : null;
                    onDispatchedTextFieldChanged({id: changedItem.id, text: changedItem._text})
                } else {
                    //updated to at least allow the special characters from build3 and bullets
                    let text = changedItem._text.replace(/[\u0250-\u2012]|[\u2028-\u206F]|[\u2070-\ue007]/g, '');
                    onTextFieldChanged({id: changedItem.id, text});

                    if(changedItem._text !== text){
                        batchSave.saveBoundData([{...changedItem, name: changedItem._name, side, text}]);
                    }
                }
            } else {
                onUnknownItemChanged();
            }
        },
        action: ({getLatestProps}) => {
            const CustomersCanvas = window["CustomersCanvas"] || null;
            ccEditor.subscribe(
                CustomersCanvas.IframeApi.PostMessage.Events.ItemChanged,
                async (changedItem) => {

                    const action = getLatestProps();

                    // remove any HTML markup provided on text fields for CC6 and above - markup already excluded with CC5
                    if ( ( changedItem.type === "BoundedTextItem" || changedItem.type === "PathBoundedTextItem" ) && ccEditor.ccVersionMajor >= 6 )
                        changedItem = stripMarkupFromTextField(changedItem);

                    action(changedItem);

                }
            );
        },
    }),
);

const stripMarkupFromTextField = (textField) => {
    if (!textField?._text) return;

    textField._text = textField._text
        // convert br and closing/start paragraph combinations to newlines
        .replace(/<br \/>/g, '\n')
        .replace(/<\/p><p>/g, '\n')
        // remove html tags
        .replace(/<[^>]+>/g, '')
        // if >, <, or & are in content, they will be represented as HTML entities - convert these characters - no other
        // characters are given this treatment, so can specifically target these two
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&');

    return textField;

}

const applyJsonToTextFields = (textFields, ccConfig) => {
    const json = ccConfig?.templateConfigurations;
    const showIsRequired = ccConfig?.showTextWarnings;
    for (let i = 0; i < textFields.length; i++) {
        const textField = textFields[i];
        // console.log({...textField});

        const textFieldSide = textField.parentContainer.parentComponent.name;
        textField['inputMask'] = gnv(json, [textFieldSide, textField.name, "inputMask"]) || "";
        textField['infoTip'] = gnv(json, [textFieldSide, textField.name, "infoTip"]) || "not found";
        textField['rule'] = gnv(json, [textFieldSide, textField.name, "rule"]) || "not found";
        textField['side'] = textFieldSide;
        textField['label'] = gnv(json, [textFieldSide, textField.name, "label"]) || textField.name;
        textField['type'] = gnv(json, [textFieldSide, textField.name, "type"]) || "not found";
        textField['index'] = gnv(json, [textFieldSide, textField.name, "index"]) || "not found";
        textField['textPermissions'] = textField.textPermissions;
        textField['originalText'] = textField.originalText;
        //V7 doesn't have isRichText value, flag is driven by allowTextFormatting permission
        textField['isRichText'] = textField._isRichText ?? textField.textPermissions.allowTextFormatting;
        textField['locked'] = textField._locked;
        textField['isRequired'] = ( showIsRequired && gnv(json, [textFieldSide, textField.name, "isRequired"]) ) || false;
    }
    return textFields;
};
